import axios from 'axios';

interface WhatsAppRecoveryRequest {
  whatsapp: string;
  metadata: {
    timestamp: string;
    ip?: string;
    userAgent: string;
    deviceType: 'mobile' | 'tablet' | 'desktop';
  };
}

interface WhatsAppRecoveryResponse {
  success: boolean;
  partial_email?: string;
  full_email?: string;
  message?: string;
  error?: string;
}

/**
 * Get device type based on screen width
 */
const getDeviceType = (): 'mobile' | 'tablet' | 'desktop' => {
  const width = window.innerWidth;
  if (width < 768) return 'mobile';
  if (width < 1024) return 'tablet';
  return 'desktop';
};

/**
 * Get user's IP address (best effort, may not work due to browser restrictions)
 */
const getUserIP = async (): Promise<string | undefined> => {
  try {
    // This is a public API that returns the client's IP
    // In production, you might want to use your own backend endpoint
    const response = await axios.get('https://api.ipify.org?format=json', {
      timeout: 3000,
    });
    return response.data.ip;
  } catch (error) {
    console.warn('[n8nWebhook] Could not fetch IP address:', error);
    return undefined;
  }
};

/**
 * Send WhatsApp recovery request to n8n webhook
 * @param whatsappNumber - User's WhatsApp number in international format
 * @returns Promise with recovery response
 */
export const sendWhatsAppRecoveryRequest = async (
  whatsappNumber: string
): Promise<WhatsAppRecoveryResponse> => {
  const webhookUrl = process.env.REACT_APP_N8N_WEBHOOK_URL;

  if (!webhookUrl) {
    console.error('[n8nWebhook] N8N webhook URL not configured');
    throw new Error(
      'Webhook de recuperação não configurado. Entre em contato com o suporte.'
    );
  }

  try {
    // Get user IP (optional, may fail due to CORS)
    const ip = await getUserIP();

    // Prepare request payload
    const payload: WhatsAppRecoveryRequest = {
      whatsapp: whatsappNumber,
      metadata: {
        timestamp: new Date().toISOString(),
        ip,
        userAgent: navigator.userAgent,
        deviceType: getDeviceType(),
      },
    };

    console.log('[n8nWebhook] Sending WhatsApp recovery request:', {
      whatsapp: whatsappNumber,
      deviceType: payload.metadata.deviceType,
      hasIP: !!ip,
    });

    // Send request to n8n webhook
    const response = await axios.post<WhatsAppRecoveryResponse>(webhookUrl, payload, {
      timeout: 30000, // 30 seconds timeout
      headers: {
        'Content-Type': 'application/json',
      },
    });

    console.log('[n8nWebhook] Webhook response received:', {
      success: response.data.success,
      hasPartialEmail: !!response.data.partial_email,
    });

    return response.data;
  } catch (error: any) {
    console.error('[n8nWebhook] Error sending WhatsApp recovery request:', {
      error,
      message: error.message,
      response: error.response?.data,
    });

    // Handle different error scenarios
    if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
      throw new Error(
        'Tempo de espera esgotado. Verifique sua conexão e tente novamente.'
      );
    }

    if (error.response) {
      // Server responded with error
      const errorMessage = error.response.data?.error || error.response.data?.message;
      throw new Error(
        errorMessage || 'Erro ao processar solicitação de recuperação. Tente novamente.'
      );
    }

    if (error.request) {
      // Request made but no response received
      throw new Error(
        'Falha na conexão com o serviço de recuperação. Verifique sua internet.'
      );
    }

    // Other errors
    throw new Error('Erro inesperado ao solicitar recuperação. Tente novamente.');
  }
};

/**
 * Validate WhatsApp number format
 * Accepts international format: +[country code][number]
 * Example: +5511999999999
 */
export const validateWhatsAppNumber = (number: string): boolean => {
  // Remove all non-digit characters except +
  const cleaned = number.replace(/[^\d+]/g, '');

  // Must start with + and have 10-15 digits (international format)
  const regex = /^\+[1-9]\d{9,14}$/;
  return regex.test(cleaned);
};

/**
 * Format WhatsApp number to international format
 * Removes spaces, dashes, parentheses
 */
export const formatWhatsAppNumber = (number: string): string => {
  // Remove all non-digit characters except +
  let cleaned = number.replace(/[^\d+]/g, '');

  // If doesn't start with +, try to add Brazil country code (+55)
  if (!cleaned.startsWith('+')) {
    // Check if it looks like a Brazilian number (11 digits)
    if (cleaned.length === 11 || cleaned.length === 10) {
      cleaned = '+55' + cleaned;
    } else {
      // For other formats, just add +
      cleaned = '+' + cleaned;
    }
  }

  return cleaned;
};
